/**
 * Financial Forecasting Types
 * LivePlan-style financial projections with what-if scenarios
 * Completely optional - existing plans work without them
 */

export type ScenarioType = 'realistic' | 'optimistic' | 'pessimistic';
export type TimelineType = 'monthly' | 'quarterly' | 'yearly' | 'mixed';

export interface RevenueStream {
  id: string;
  name: string;
  category: 'product' | 'service' | 'subscription' | 'other';
  description?: string;
  // Projections by period
  projections: number[];
  // Growth rate percentage
  growthRate: number;
  // Unit economics
  unitPrice?: number;
  unitsPerPeriod?: number;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  category: 'fixed' | 'variable' | 'one-time';
  description?: string;
  // Projections by period
  projections: number[];
  // Growth rate percentage
  growthRate?: number;
}

export interface CapexItem {
  id: string;
  name: string;
  amount: number;
  purchasePeriodNumber: number;
  usefulLifeMonths: number;
}

export interface FinancialAssumptions {
  // Revenue assumptions
  initialRevenue: number;
  revenueGrowthRate: number;
  seasonalityFactors?: number[]; // Monthly multipliers
  
  // Expense assumptions
  initialExpenses: number;
  expenseGrowthRate: number;
  fixedCosts: number;
  variableCostPercentage: number;
  
  // Other assumptions
  taxRate: number;
  investmentAmount?: number;
  loanAmount?: number;
  interestRate?: number;
  loanTermMonths?: number;

  arDays?: number;
  apDays?: number;
  inventoryDays?: number;

  capexItems?: CapexItem[];
}

export interface FinancialProjection {
  period: string; // e.g., "2025-Q1", "Jan 2025", "Year 1"
  periodNumber: number; // 1, 2, 3...
  
  // Revenue
  totalRevenue: number;
  revenueByStream: Record<string, number>;
  
  // Expenses
  totalExpenses: number;
  expenseByCategory: Record<string, number>;
  
  // Profitability
  grossProfit: number;
  netProfit: number;
  profitMargin: number;

  depreciationExpense?: number;
  interestExpense?: number;
  
  // Cash flow
  cashFlow: number;
  cumulativeCashFlow: number;

  capex?: number;
  principalRepayment?: number;

  cashBalance?: number;

  accountsReceivable?: number;
  accountsPayable?: number;
  inventory?: number;
  workingCapitalChange?: number;
  currentAssets?: number;
  currentLiabilities?: number;
  fixedAssetsGross?: number;
  accumulatedDepreciation?: number;
  netFixedAssets?: number;
  totalAssets?: number;
  totalLiabilities?: number;
  loanBalance?: number;
  totalEquity?: number;
  retainedEarnings?: number;
  contributedCapital?: number;
  balanceSheetTieOut?: number;
  
  // Metrics
  breakEven: boolean;
  burnRate?: number;
  runway?: number; // months remaining
}

export interface ScenarioProjections {
  realistic: FinancialProjection[];
  optimistic: FinancialProjection[];
  pessimistic: FinancialProjection[];
}

export interface IndustryBenchmark {
  metricName: string;
  yourValue: number;
  industryAverage: number;
  industryMedian: number;
  topQuartile: number;
  unit: string; // '%', '$', 'ratio', etc.
  status: 'above' | 'average' | 'below';
  recommendation?: string;
}

export interface FinancialMetrics {
  // Profitability metrics
  grossMargin: number;
  netMargin: number;
  ebitda: number;
  
  // Liquidity metrics
  currentRatio?: number;
  quickRatio?: number;
  dscr?: number;
  
  // Efficiency metrics
  revenuePerEmployee?: number;
  customerAcquisitionCost?: number;
  customerLifetimeValue?: number;
  
  // Growth metrics
  revenueGrowthRate: number;
  customerGrowthRate?: number;
  
  // Break-even analysis
  breakEvenRevenue: number;
  breakEvenUnits?: number;
  monthsToBreakEven: number;
}

export interface FinancialForecast {
  id: string;
  business_plan_id: string;
  user_id: string;
  
  // Configuration
  timeline: TimelineType;
  periodsCount: number; // e.g., 36 months, 12 quarters, 5 years
  startDate: string;
  
  // Assumptions
  assumptions: FinancialAssumptions;
  
  // Revenue streams
  revenueStreams: RevenueStream[];
  
  // Expense categories
  expenseCategories: ExpenseCategory[];
  
  // Projections for all scenarios
  projections: ScenarioProjections;
  
  // Key metrics
  metrics: FinancialMetrics;
  
  // Industry benchmarks (optional)
  industryBenchmarks?: IndustryBenchmark[];
  
  // Metadata
  created_at: string;
  updated_at: string;
  last_calculated_at: string;
}

export interface ForecastCalculationInput {
  timeline: TimelineType;
  periodsCount: number;
  startDate: string;
  assumptions: FinancialAssumptions;
  revenueStreams: RevenueStream[];
  expenseCategories: ExpenseCategory[];
  industry?: string; // For benchmark comparison
}

export interface WhatIfScenario {
  name: string;
  description: string;
  changes: {
    revenueGrowthRate?: number;
    expenseGrowthRate?: number;
    investmentAmount?: number;
    // ... other adjustable parameters
  };
  results: {
    netProfitChange: number;
    breakEvenChange: number;
    cashFlowImpact: number;
  };
}

// Default starter assumptions for new forecasts
export const DEFAULT_FINANCIAL_ASSUMPTIONS: FinancialAssumptions = {
  initialRevenue: 0,
  revenueGrowthRate: 15, // 15% growth
  initialExpenses: 0,
  expenseGrowthRate: 10, // 10% expense growth
  fixedCosts: 0,
  variableCostPercentage: 30, // 30% of revenue
  taxRate: 25, // 25% tax rate
  capexItems: [],
  loanTermMonths: 60,
  arDays: 0,
  apDays: 0,
  inventoryDays: 0,
};

// Industry benchmark categories
export const INDUSTRY_BENCHMARK_METRICS = [
  'Gross Margin',
  'Net Margin',
  'Operating Margin',
  'Revenue Growth Rate',
  'Customer Acquisition Cost',
  'Customer Lifetime Value',
  'LTV/CAC Ratio',
  'Burn Rate',
  'Runway',
  'Break-even Time'
] as const;

export type IndustryBenchmarkMetric = typeof INDUSTRY_BENCHMARK_METRICS[number];
